import { useState, useCallback } from 'react';
import { useHttpClient } from '@actschurch/ccms-sdk';
import { getApiUrl } from '../../../../config/ccms';
import type {
  RedisHealth,
  CacheStatistics,
  ServiceKeyGroup,
  DomainStatistics,
  PatternPreviewResult,
} from '../types';

export function useRedisMonitoring() {
  const httpClient = useHttpClient();
  const [health, setHealth] = useState<RedisHealth | null>(null);
  const [statistics, setStatistics] = useState<CacheStatistics | null>(null);
  const [serviceGroups, setServiceGroups] = useState<Record<string, ServiceKeyGroup>>({});
  const [domainStats, setDomainStats] = useState<Record<string, DomainStatistics>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHealth = useCallback(async () => {
    try {
      const response = await httpClient.get<RedisHealth>(
        getApiUrl('system', '/api/redismanagement/health')
      );
      setHealth(response.data ?? null);
    } catch (err: any) {
      console.error('Error fetching Redis health:', err);
      throw err;
    }
  }, [httpClient]);

  const fetchStatistics = useCallback(async () => {
    try {
      const response = await httpClient.get<CacheStatistics>(
        getApiUrl('system', '/api/redismanagement/statistics')
      );
      setStatistics(response.data ?? null);
    } catch (err: any) {
      console.error('Error fetching Redis statistics:', err);
      throw err;
    }
  }, [httpClient]);

  const fetchServiceGroups = useCallback(async () => {
    try {
      const response = await httpClient.get<{ services: Record<string, ServiceKeyGroup> }>(
        getApiUrl('system', '/api/redismanagement/keys/by-service')
      );
      setServiceGroups(response.data?.services ?? {});
    } catch (err: any) {
      console.error('Error fetching service groups:', err);
      throw err;
    }
  }, [httpClient]);

  const fetchDomainStats = useCallback(async () => {
    try {
      const response = await httpClient.get<Record<string, DomainStatistics>>(
        getApiUrl('system', '/api/redismanagement/statistics/by-domain')
      );
      setDomainStats(response.data ?? {});
    } catch (err: any) {
      console.error('Error fetching domain stats:', err);
      throw err;
    }
  }, [httpClient]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      await Promise.all([fetchHealth(), fetchStatistics(), fetchServiceGroups(), fetchDomainStats()]);
    } catch (err: any) {
      // Check if it's a 404 error indicating backend not available
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        setError('Redis management endpoints not available. Please start the local CCMS.Api.System backend service or update the proxy configuration to route /system/* to your local API.');
      } else {
        setError(err.message || 'Failed to fetch Redis data');
      }
    } finally {
      setLoading(false);
    }
  }, [fetchHealth, fetchStatistics, fetchServiceGroups, fetchDomainStats]);

  const previewPatternDeletion = useCallback(
    async (pattern: string): Promise<PatternPreviewResult> => {
      const response = await httpClient.post<PatternPreviewResult>(
        getApiUrl('system', '/api/redismanagement/keys/preview-deletion'),
        { pattern }
      );
      return (
        response.data ?? { matchCount: 0, sampleKeys: [], isDangerous: false, warnings: ['No response from server'] }
      );
    },
    [httpClient]
  );

  const deleteByPattern = useCallback(
    async (pattern: string) => {
      await httpClient.post(getApiUrl('system', '/api/redismanagement/clear-by-pattern'), { pattern });
    },
    [httpClient]
  );

  const deleteKey = useCallback(
    async (key: string) => {
      await httpClient.delete(getApiUrl('system', `/api/redismanagement/keys/${key}`));
    },
    [httpClient]
  );

  return {
    health,
    statistics,
    serviceGroups,
    domainStats,
    loading,
    error,
    setError,
    fetchAll,
    previewPatternDeletion,
    deleteByPattern,
    deleteKey,
  };
}
